/**
 * lib/compliancePdf.ts
 * ------------------------------------------------------------------
 * Printable "Marks-Entry Compliance" summary PDF — a chase document
 * grouped by teacher, listing each incomplete (class · subject) and the
 * names of the students still missing that CA's marks.
 *
 * Uses the shared LKC PDF chrome (pdfChrome.ts) so it matches every
 * other report. Pure builder: returns a jsPDF; the caller saves it.
 * ------------------------------------------------------------------ */
import { jsPDF } from 'jspdf';
import autoTable, { type CellHookData, type RowInput, type Styles } from 'jspdf-autotable';
import { PDF, CONTENT_TOP, pageGeom, drawLetterhead, stampFooters } from '@/lib/pdfChrome';
import type { ComplianceRow } from '@/lib/marksCompliance';

export interface CompliancePdfData {
  termLabel: string;
  year: string;
  caLabel: string;
  deadline?: string;
  overdueDays: number | null;
  deadlinePassed: boolean;
  summary: { total: number; complete: number; partial: number; notStarted: number; nonCompliant: number };
  /** Teachers with at least one incomplete group, most-incomplete first. */
  teachers: { name: string; incompleteRows: ComplianceRow[]; missingStudents: number }[];
  logoDataUrl?: string | null;
  generatedBy?: string;
}

export function buildCompliancePdf(data: CompliancePdfData): jsPDF {
  const geom = pageGeom('p');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

  const deadlineStr = data.deadline
    ? data.deadlinePassed
      ? `deadline ${data.deadline} — ${data.overdueDays}d overdue`
      : `deadline ${data.deadline}`
    : 'no deadline set';
  const metaLine = `${data.termLabel} ${data.year}  ·  CA: ${data.caLabel}  ·  ${deadlineStr}`;
  const summaryLine =
    `Assignments ${data.summary.total}   ·   Complete ${data.summary.complete}   ·   ` +
    `Partial ${data.summary.partial}   ·   Not started ${data.summary.notStarted}` +
    (data.deadlinePassed ? `   ·   Non-compliant ${data.summary.nonCompliant}` : '');

  const head: RowInput = ['Class · Subject', 'Missing', 'Students not entered'];
  const body: RowInput[] = [];
  const bandRows = new Set<number>();

  if (data.teachers.length === 0) {
    body.push([{ content: 'All assignments complete for this CA — nothing outstanding.', colSpan: 3, styles: { halign: 'center', textColor: PDF.MUTED } }]);
  } else {
    for (const t of data.teachers) {
      bandRows.add(body.length);
      body.push([{
        content: `${t.name}  —  ${t.incompleteRows.length} class${t.incompleteRows.length === 1 ? '' : 'es'} missing, ${t.missingStudents} student${t.missingStudents === 1 ? '' : 's'}`,
        colSpan: 3,
        styles: { fontStyle: 'bold', fillColor: PDF.BAND, textColor: PDF.NAVY },
      }]);
      for (const r of t.incompleteRows) {
        body.push([
          `${r.className} · ${r.subjectName}`,
          `${r.missing.length} of ${r.total}`,
          r.missing.map((s) => s.name).join(', ') || '—',
        ]);
      }
    }
  }

  autoTable(pdf, {
    head: [head],
    body,
    startY: CONTENT_TOP + 8,
    margin: { top: CONTENT_TOP + 8, left: geom.MX, right: geom.MX, bottom: 14 },
    theme: 'plain',
    styles: { font: 'helvetica', fontSize: 8, textColor: PDF.INK, cellPadding: { top: 1.4, bottom: 1.4, left: 2, right: 2 }, lineColor: PDF.RULE, lineWidth: 0.1, overflow: 'linebreak', valign: 'top' },
    headStyles: { fillColor: PDF.NAVY, textColor: PDF.WHITE, fontStyle: 'bold', fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 52 },
      1: { cellWidth: 20, halign: 'center' },
      2: { cellWidth: 'auto' },
    } as Record<number, Partial<Styles>>,
    didParseCell: (h: CellHookData) => {
      if (h.section === 'body' && bandRows.has(h.row.index)) return; // band styled inline
      if (h.section === 'body' && h.column.index === 2) h.cell.styles.textColor = PDF.MUTED;
    },
    didDrawPage: (h) => {
      drawLetterhead(pdf, { title: 'MARKS-ENTRY COMPLIANCE', metaLine, logoDataUrl: data.logoDataUrl, geom });
      if (h.pageNumber === 1) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(...PDF.MUTED);
        pdf.text(summaryLine, geom.MX, CONTENT_TOP + 3);
      }
    },
  });

  stampFooters(pdf, { geom, generatedBy: data.generatedBy });
  return pdf;
}
